import { appAlert, appPromptPassphrase } from './appDialog';
import { copyTextToClipboard } from './formatListClipboard';
import {
  buildShareDecryptUrl,
  decryptShareBlob,
  encryptShareBlob,
  MAX_SHARE_PARAM_CHARS,
  MAX_SHARE_URL_CHARS,
  parseShareLinkFromHref,
} from './shareLinkCrypto';
import { assertSharePayloadSafe } from './shareContentSafety';
import {
  parseSharePlain,
  serializeSharePlain,
  type ShareKind,
  type SharePlainPayload,
} from './sharePayload';

function shareTooLongHelp(kind: ShareKind, encodedLen: number): string {
  const max = MAX_SHARE_PARAM_CHARS.toLocaleString();
  const got = encodedLen.toLocaleString();
  const base = `The encrypted link is ${got} characters, but the maximum is ${max} (browser and server limits).`;
  if (kind === 'page') {
    return `${base}\n\nTry generating a link for one section or a single item instead of the whole page.`;
  }
  if (kind === 'section') {
    return `${base}\n\nTry generating a link for one item, or split the section into smaller parts.`;
  }
  return `${base}\n\nTry shortening the item text before generating a link.`;
}

function formatGenerateError(err: unknown, kind: ShareKind): string {
  if (err instanceof Error && err.message.startsWith('LINK_TOO_LONG:')) {
    const n = parseInt(err.message.slice('LINK_TOO_LONG:'.length), 10);
    return shareTooLongHelp(kind, Number.isFinite(n) ? n : MAX_SHARE_PARAM_CHARS + 1);
  }
  if (err instanceof Error && err.message.startsWith('LINK_URL_TOO_LONG:')) {
    const n = parseInt(err.message.slice('LINK_URL_TOO_LONG:'.length), 10);
    const max = MAX_SHARE_URL_CHARS.toLocaleString();
    const got = Number.isFinite(n) ? n.toLocaleString() : 'too many';
    return `The full link is ${got} characters (max ${max}). Use a shorter passphrase or share less text.`;
  }
  return err instanceof Error ? err.message : 'Could not generate link.';
}

/** Navigate the browser to a share URL (full load → App shows ShareDecryptView). */
export function openShareDecryptUrl(url: string): void {
  const parsed = parseShareLinkFromHref(url);
  if (!parsed) {
    void appAlert('This does not look like a valid encrypted share link.', 'Invalid link');
    return;
  }
  const target = new URL('/', window.location.origin);
  target.searchParams.set('s', parsed.payload);
  if (parsed.key) target.searchParams.set('k', parsed.key);
  else target.searchParams.delete('k');
  window.location.assign(target.toString());
}

export type GenerateShareLinkResult = { url: string };

export async function generateAndCopyShareLink(
  plain: SharePlainPayload
): Promise<GenerateShareLinkResult | null> {
  if (!plain.body.trim()) {
    await appAlert('Nothing to share — there is no text content.', 'Generate link');
    return null;
  }

  const prompt = await appPromptPassphrase({
    title: 'Generate encrypted link',
    message: 'Choose a passphrase. Recipients need it to decrypt the content.',
    confirmLabel: 'Generate & copy',
    includeKeyInUrlLabel: 'Include passphrase in the link',
    includeKeyInUrlWarning:
      'This makes a public link: anyone with the link can view the content without entering the passphrase.',
    includeKeyInUrlDefault: false,
  });
  if (prompt === null) return null;

  try {
    const json = serializeSharePlain(plain);
    const encoded = await encryptShareBlob(json, prompt.passphrase);
    const url = buildShareDecryptUrl(encoded, prompt.passphrase, {
      includeKeyInUrl: prompt.includeKeyInUrl,
    });
    await copyTextToClipboard(url);

    if (prompt.includeKeyInUrl) {
      await appAlert(
        'Public link copied. Opening this link shows the decrypt screen (same as other share links).',
        'Link copied'
      );
    } else {
      await appAlert(
        'Link copied. Opening the link shows the decrypt screen; recipients enter the passphrase you chose.',
        'Link copied'
      );
    }
    return { url };
  } catch (err) {
    await appAlert(formatGenerateError(err, plain.kind), 'Generate link failed');
    return null;
  }
}

export async function decryptShareLinkPayload(encoded: string, passphrase: string): Promise<SharePlainPayload> {
  const json = await decryptShareBlob(encoded, passphrase);
  return assertSharePayloadSafe(parseSharePlain(json));
}
